/**
 * Wizard to import PAM evidence (and preview moving PAM actions) into a safety group.
 * Confirm moves personal/non-org files into this group's library (owned),
 * links organisation-library files in place, attaches files and notes
 * to the selected evidence requirements, and assigns any unassigned selected
 * SAQs to this group so their PAM costed actions follow.
 */

import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { BuildingIcon, FileTextIcon, LinkSimpleIcon, NotePencilIcon, UserIcon, XIcon } from '@phosphor-icons/react';
import Modal from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import Combobox from '@/components/ui/Combobox';
import Dropdown from '@/components/ui/Dropdown';
import Loading from '@/components/ui/Loading';
import Pill from '@/components/ui/Pill';
import { toast } from '@/components/ui/toast';
import { apiGet } from '@/lib/api/axios';
import { queryKeys } from '../hooks';
import { importGroupStatutoryEvidence, type StatutoryDocumentRow } from '../api';
import * as hubApi from 'src/features/document-hub/api';
import { isOrganisationLibraryNode } from 'src/features/document-hub/types';
import * as pamApi from 'src/features/pam/api/client';
import { pamQueryKeys, usePamGroupSaqs, usePamQuestionsOverview } from 'src/features/pam/api/hooks';
import type {
  PamCostedAction,
  PamCostedActionsListResponse,
  PamEvidence,
  PamEvidenceDocument,
  PamSubmissionListItem
} from 'src/features/pam/api/types';
import PamCostedActionRow from 'src/features/pam/PamCostedActionRow';
import { getEfmDomainLabel, type EFMDomainCode } from 'src/features/domains/efmDomains';
import { pamSaqAcronymForEfmDomain } from './efmDomainPamSaq';

type Step = 1 | 2 | 3;

/** Matches StatutoryImportRequest.notes max_length on the import endpoint. */
const STATUTORY_IMPORT_NOTE_BATCH_SIZE = 500;

interface SaqOption {
  submission_id: string;
  submission_saq_id: string;
  acronym: string;
  name: string;
  assignedToThisGroup: boolean;
  canOpen: boolean;
  canAssign: boolean;
}

interface Props {
  open: boolean;
  onClose: () => void;
  groupId: string;
  groupName: string;
  domain: EFMDomainCode | null;
  documents: StatutoryDocumentRow[];
}

function saqAcronymFromQuestionRef(ref: string): string {
  const dot = ref.lastIndexOf('.');
  return dot > 0 ? ref.slice(0, dot) : ref;
}

function suggestedSaqAcronym(documents: StatutoryDocumentRow[]): string | null {
  const counts = new Map<string, number>();
  for (const document of documents) {
    for (const question of document.pam_questions) {
      const acronym = saqAcronymFromQuestionRef(question.ref);
      counts.set(acronym, (counts.get(acronym) ?? 0) + 1);
    }
  }
  let best: string | null = null;
  let bestCount = 0;
  for (const [acronym, count] of counts) {
    if (count > bestCount) {
      best = acronym;
      bestCount = count;
    }
  }
  return best;
}

function defaultImportSubmissionId(submissions: PamSubmissionListItem[], groupSubmissionId: string): string {
  if (groupSubmissionId !== '' && submissions.some((item) => item.submission_id === groupSubmissionId)) {
    return groupSubmissionId;
  }
  const inProgress = submissions.filter((item) => item.status === 'IN_PROGRESS');
  if (inProgress.length > 0) return inProgress[0].submission_id;
  return submissions[0]?.submission_id ?? '';
}

function submissionOptionLabel(item: PamSubmissionListItem): string {
  const status =
    item.status === 'IN_PROGRESS' ? 'In progress' : item.status === 'FINALISED' ? 'Finalised' : 'Submitted';
  return `${item.title} · ${item.iteration.year} · ${status}`;
}

function filesForRequirement(files: PamEvidenceDocument[], requirement: StatutoryDocumentRow): PamEvidenceDocument[] {
  const refs = new Set(requirement.pam_questions.map((question) => question.ref));
  const byId = new Map<string, PamEvidenceDocument>();
  for (const file of files) {
    if (file.questions.some((question) => refs.has(question.ref))) {
      byId.set(file.document_id, file);
    }
  }
  return [...byId.values()];
}

function isOrgLibraryFile(file: PamEvidenceDocument): boolean {
  return isOrganisationLibraryNode({
    document_type: file.document_type,
    visibility: file.visibility
  });
}

interface PamImportNote extends PamEvidence {
  question_ref: string;
}

function otherEvidenceLabel(item: PamEvidence): string {
  if (item.text != null && item.text !== '') return item.text;
  if (item.external_link != null && item.external_link !== '') return item.external_link;
  if (item.contact_name != null && item.contact_name !== '') return item.contact_name;
  return 'Note';
}

function otherEvidenceIcon(item: PamEvidence) {
  if (item.external_link != null && item.external_link !== '') return LinkSimpleIcon;
  if (item.contact_name != null && item.contact_name !== '') return UserIcon;
  return NotePencilIcon;
}

function noteAlreadyOnRequirement(requirement: StatutoryDocumentRow, note: PamImportNote): boolean {
  return requirement.other_evidence.some(
    (existing) =>
      (note.text != null && note.text !== '' && existing.text === note.text) ||
      (note.external_link != null && note.external_link !== '' && existing.external_link === note.external_link)
  );
}

function notesForRequirement(notes: PamImportNote[], requirement: StatutoryDocumentRow): PamImportNote[] {
  const refs = new Set(requirement.pam_questions.map((question) => question.ref));
  const byId = new Map<string, PamImportNote>();
  for (const note of notes) {
    if (refs.has(note.question_ref)) byId.set(note.id, note);
  }
  return [...byId.values()];
}

function requirementHasPamItems(
  files: PamEvidenceDocument[],
  notes: PamImportNote[],
  requirement: StatutoryDocumentRow
): boolean {
  return filesForRequirement(files, requirement).length > 0 || notesForRequirement(notes, requirement).length > 0;
}

function fileStatusLabel(file: PamEvidenceDocument, alreadyLinked: boolean): string {
  if (alreadyLinked) return 'Already attached';
  if (isOrgLibraryFile(file)) return 'Linked';
  return 'Moves to group';
}

function PamImportRequirementRow({
  requirement,
  pamFiles,
  pamNotes,
  fileSelections,
  noteSelections,
  muted,
  onToggleFile,
  onToggleNote
}: {
  requirement: StatutoryDocumentRow;
  pamFiles: PamEvidenceDocument[];
  pamNotes: PamImportNote[];
  fileSelections: Record<string, string[]>;
  noteSelections: Record<string, string[]>;
  muted: boolean;
  onToggleFile: (recommendationId: string, documentId: string) => void;
  onToggleNote: (recommendationId: string, noteId: string) => void;
}) {
  const candidates = filesForRequirement(pamFiles, requirement);
  const noteCandidates = notesForRequirement(pamNotes, requirement);
  const selected = new Set(fileSelections[requirement.recommendation_id] ?? []);
  const selectedNotes = new Set(noteSelections[requirement.recommendation_id] ?? []);
  const linked = new Set(requirement.linked_documents.map((linkedDoc) => linkedDoc.id));
  const pamRefs = [...new Set(requirement.pam_questions.map((question) => question.ref))];
  const nameClass = muted ? 'text-content-subtle' : 'text-content-default';
  return (
    <tr>
      <td className={`align-top px-3 py-2.5 text-sm font-medium ${nameClass}`}>
        {requirement.name}
        {!requirement.required && (
          <span className="mt-0.5 block text-xs font-normal text-content-subtle">Optional</span>
        )}
      </td>
      <td className="align-top px-3 py-2.5">
        <div className="flex flex-wrap gap-1">
          {pamRefs.map((ref) => (
            <Pill key={ref} variant="info" size="sm" className={muted ? 'opacity-70' : undefined}>
              {ref}
            </Pill>
          ))}
        </div>
      </td>
      <td className="align-top px-3 py-2.5">
        {muted ? (
          <span className="text-xs text-content-subtle">None</span>
        ) : (
          <ul className="flex list-none flex-col gap-1 p-0">
            {candidates.map((file) => (
              <li key={file.document_id}>
                <label className="flex cursor-pointer items-center gap-2 text-sm text-content-default">
                  <Checkbox
                    checked={selected.has(file.document_id)}
                    onChange={() => onToggleFile(requirement.recommendation_id, file.document_id)}
                    aria-label={`Attach ${file.name} to ${requirement.name}`}
                  />
                  <FileTextIcon size={16} className="shrink-0 text-content-subtle" />
                  <span className="min-w-0 truncate">{file.name}</span>
                  <span className="shrink-0 text-xs text-content-subtle">
                    {fileStatusLabel(file, linked.has(file.document_id))}
                  </span>
                </label>
              </li>
            ))}
            {noteCandidates.map((note) => {
              const NoteIcon = otherEvidenceIcon(note);
              return (
                <li key={note.id}>
                  <label className="flex cursor-pointer items-center gap-2 text-sm text-content-default">
                    <Checkbox
                      checked={selectedNotes.has(note.id)}
                      onChange={() => onToggleNote(requirement.recommendation_id, note.id)}
                      aria-label={`Attach ${otherEvidenceLabel(note)} to ${requirement.name}`}
                    />
                    <NoteIcon size={16} weight="light" className="shrink-0 text-content-subtle" />
                    <span className="min-w-0 truncate">{otherEvidenceLabel(note)}</span>
                    {noteAlreadyOnRequirement(requirement, note) && (
                      <span className="shrink-0 text-xs text-content-subtle">Already attached</span>
                    )}
                  </label>
                </li>
              );
            })}
          </ul>
        )}
      </td>
    </tr>
  );
}

async function fetchAllPamEvidenceDocuments(submissionId: string): Promise<PamEvidenceDocument[]> {
  const pageSize = 100;
  const byId = new Map<string, PamEvidenceDocument>();
  let page = 1;
  let total = Infinity;
  while (byId.size < total) {
    const response = await pamApi.getPamEvidenceDocuments(submissionId, null, page, pageSize);
    total = response.total;
    for (const item of response.items) byId.set(item.document_id, item);
    if (response.items.length === 0 || response.items.length < pageSize) break;
    page += 1;
  }
  return [...byId.values()];
}

async function fetchPamOtherNotes(submissionId: string, questionIds: string[]): Promise<PamImportNote[]> {
  if (questionIds.length === 0) return [];
  const notes = await pamApi.getPamOtherEvidence(submissionId, questionIds);
  return notes.map((note) => ({
    id: note.id,
    document_id: null,
    document_title: null,
    text: note.text,
    contact_name: note.contact_name,
    contact_details: note.contact_details,
    external_link: note.external_link,
    created_by: null,
    created_at: '',
    updated_at: '',
    question_ref: note.question_ref
  }));
}

export default function ImportPamModal({ open, onClose, groupId, groupName, domain, documents }: Props) {
  const saqSearchId = useId();
  const queryClient = useQueryClient();
  const [step, setStep] = useState<Step>(1);
  const [saqQuery, setSaqQuery] = useState('');
  const [selectedSaqIds, setSelectedSaqIds] = useState<string[]>([]);
  const [fileSelections, setFileSelections] = useState<Record<string, string[]>>({});
  const [noteSelections, setNoteSelections] = useState<Record<string, string[]>>({});
  const [importing, setImporting] = useState(false);
  const [submissionOverride, setSubmissionOverride] = useState<string | null>(null);
  const didPrefillSaqs = useRef(false);

  const groupSaqsQuery = usePamGroupSaqs(groupId, { enabled: open && groupId !== '' });
  const groupSaqs = groupSaqsQuery.data?.saqs ?? [];
  const submissionsQuery = useQuery({
    queryKey: pamQueryKeys.submissions(),
    queryFn: () => pamApi.getPamSubmissions(),
    enabled: open,
    meta: { globalError: false, context: 'import-pam-submissions' }
  });
  const submissions = submissionsQuery.data?.items ?? [];
  const groupSubmissionId = groupSaqs[0]?.submission_id ?? '';
  const submissionId = submissionOverride ?? defaultImportSubmissionId(submissions, groupSubmissionId);

  const overviewQuery = usePamQuestionsOverview(open ? submissionId : '', { includeInaccessibleSaqs: true });

  const saqOptions: SaqOption[] = useMemo(() => {
    const assignedIds = new Set(groupSaqs.map((saq) => saq.submission_saq_id));
    const byId = new Map<string, SaqOption>();
    const options: SaqOption[] = [];
    const pushOption = (option: SaqOption) => {
      if (byId.has(option.submission_saq_id)) return;
      byId.set(option.submission_saq_id, option);
      options.push(option);
    };
    for (const pamDomain of overviewQuery.data?.domains ?? []) {
      const saqs = [...pamDomain.saqs].sort((left, right) => left.sort_order - right.sort_order);
      for (const saq of saqs) {
        pushOption({
          submission_id: submissionId,
          submission_saq_id: saq.submission_saq_id,
          acronym: saq.acronym,
          name: saq.name,
          assignedToThisGroup: assignedIds.has(saq.submission_saq_id),
          canOpen: saq.can_open,
          canAssign: saq.can_assign
        });
      }
    }
    for (const saq of groupSaqs.filter((saq) => saq.submission_id === submissionId)) {
      pushOption({
        submission_id: saq.submission_id,
        submission_saq_id: saq.submission_saq_id,
        acronym: saq.acronym,
        name: saq.name,
        assignedToThisGroup: true,
        canOpen: true,
        canAssign: true
      });
    }
    const mappedAcronym = pamSaqAcronymForEfmDomain(domain);
    if (mappedAcronym && !options.some((saq) => saq.acronym === mappedAcronym)) {
      pushOption({
        submission_id: submissionId,
        submission_saq_id: `domain-default:${mappedAcronym}`,
        acronym: mappedAcronym,
        name: domain ? `${getEfmDomainLabel(domain)} safety group` : mappedAcronym,
        assignedToThisGroup: false,
        canOpen: false,
        canAssign: false
      });
    }
    return options;
  }, [groupSaqs, overviewQuery.data?.domains, submissionId, domain]);

  const suggestedAcronym = pamSaqAcronymForEfmDomain(domain) ?? suggestedSaqAcronym(documents);

  useEffect(() => {
    if (!open) return;
    setStep(1);
    setSaqQuery('');
    setSelectedSaqIds([]);
    setFileSelections({});
    setNoteSelections({});
    setImporting(false);
    setSubmissionOverride(null);
    didPrefillSaqs.current = false;
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setSelectedSaqIds((current) => {
      let changed = false;
      const next = current.map((id) => {
        if (!id.startsWith('domain-default:')) return id;
        const acronym = id.slice('domain-default:'.length);
        const real = saqOptions.find(
          (saq) => saq.acronym === acronym && !saq.submission_saq_id.startsWith('domain-default:')
        );
        if (real == null) return id;
        changed = true;
        return real.submission_saq_id;
      });
      return changed ? next : current;
    });
  }, [open, saqOptions]);

  useEffect(() => {
    if (!open || didPrefillSaqs.current) return;
    const suggested = suggestedAcronym ? saqOptions.find((saq) => saq.acronym === suggestedAcronym) : undefined;
    const waitingForCatalogue =
      Boolean(suggestedAcronym) &&
      (suggested == null || suggested.submission_saq_id.startsWith('domain-default:')) &&
      (overviewQuery.isFetching || (submissionId === '' && submissionsQuery.isFetching));
    if (waitingForCatalogue) return;
    if (saqOptions.length === 0 && (groupSaqsQuery.isFetching || overviewQuery.isFetching)) return;
    const assignedIds = saqOptions
      .filter((saq) => saq.assignedToThisGroup && saq.canOpen)
      .map((saq) => saq.submission_saq_id);
    const prefill = [...assignedIds];
    if (suggested != null && suggested.canOpen && !prefill.includes(suggested.submission_saq_id)) {
      prefill.push(suggested.submission_saq_id);
    }
    didPrefillSaqs.current = true;
    if (prefill.length > 0) setSelectedSaqIds(prefill);
  }, [
    open,
    saqOptions,
    suggestedAcronym,
    overviewQuery.isFetching,
    submissionsQuery.isFetching,
    groupSaqsQuery.isFetching,
    submissionId
  ]);

  const selectedSaqs = useMemo(
    () =>
      selectedSaqIds
        .map((id) => saqOptions.find((saq) => saq.submission_saq_id === id))
        .filter((saq): saq is SaqOption => saq != null),
    [selectedSaqIds, saqOptions]
  );

  const addableSaqOptions = useMemo(
    () => saqOptions.filter((saq) => !selectedSaqIds.includes(saq.submission_saq_id)),
    [saqOptions, selectedSaqIds]
  );

  const requirementsForSaq = useMemo(() => {
    const acronyms = new Set(selectedSaqs.map((saq) => saq.acronym));
    if (acronyms.size === 0) return [];
    return documents.filter((document) =>
      document.pam_questions.some((question) => acronyms.has(saqAcronymFromQuestionRef(question.ref)))
    );
  }, [documents, selectedSaqs]);

  const primarySubmissionId = selectedSaqs.find((saq) => saq.submission_id)?.submission_id ?? submissionId;

  const evidenceQuery = useQuery({
    queryKey: ['import-pam-evidence', primarySubmissionId],
    queryFn: () => fetchAllPamEvidenceDocuments(primarySubmissionId),
    enabled: open && step >= 2 && primarySubmissionId !== ''
  });

  const pamFiles = evidenceQuery.data ?? [];

  const mappedQuestionIds = useMemo(() => {
    const refs = new Set(requirementsForSaq.flatMap((requirement) => requirement.pam_questions.map((q) => q.ref)));
    const selected = new Set(selectedSaqIds);
    const ids = new Set<string>();
    for (const saq of groupSaqs) {
      if (!selected.has(saq.submission_saq_id)) continue;
      for (const question of saq.questions) {
        if (refs.has(question.ref)) ids.add(question.id);
      }
    }
    for (const question of overviewQuery.data?.questions ?? []) {
      if (!selected.has(question.submission_saq_id)) continue;
      if (refs.has(question.ref)) ids.add(question.submission_question_id);
    }
    return [...ids];
  }, [requirementsForSaq, selectedSaqIds, groupSaqs, overviewQuery.data?.questions]);

  const notesQuery = useQuery({
    queryKey: ['import-pam-notes', primarySubmissionId, mappedQuestionIds],
    queryFn: () => fetchPamOtherNotes(primarySubmissionId, mappedQuestionIds),
    enabled:
      open && step >= 2 && primarySubmissionId !== '' && mappedQuestionIds.length > 0 && !overviewQuery.isFetching,
    meta: { globalError: false, context: 'import-pam-notes' }
  });
  const pamNotes = notesQuery.data ?? [];

  useEffect(() => {
    if (step !== 2 || evidenceQuery.isFetching) return;
    setFileSelections((current) => {
      if (Object.keys(current).length > 0) return current;
      const next: Record<string, string[]> = {};
      for (const requirement of requirementsForSaq) {
        const candidates = filesForRequirement(pamFiles, requirement);
        const alreadyLinked = new Set(requirement.linked_documents.map((linked) => linked.id));
        const linkedOnPam = candidates
          .filter((file) => alreadyLinked.has(file.document_id))
          .map((file) => file.document_id);
        if (linkedOnPam.length > 0) {
          next[requirement.recommendation_id] = linkedOnPam;
        } else if (candidates.length === 1) {
          next[requirement.recommendation_id] = [candidates[0].document_id];
        } else {
          next[requirement.recommendation_id] = [];
        }
      }
      return next;
    });
  }, [step, evidenceQuery.isFetching, requirementsForSaq, pamFiles]);

  useEffect(() => {
    if (step !== 2 || notesQuery.isFetching) return;
    setNoteSelections((current) => {
      if (Object.keys(current).length > 0) return current;
      const next: Record<string, string[]> = {};
      for (const requirement of requirementsForSaq) {
        const candidates = notesForRequirement(pamNotes, requirement);
        next[requirement.recommendation_id] = candidates.map((note) => note.id);
      }
      return next;
    });
  }, [step, notesQuery.isFetching, requirementsForSaq, pamNotes]);

  const costedQuery = useQuery({
    queryKey: [...pamQueryKeys.costedActions(primarySubmissionId), 'import-pam', selectedSaqIds],
    queryFn: async () => {
      const response = await apiGet<PamCostedActionsListResponse>(
        `/pam/submissions/${primarySubmissionId}/costed-actions`,
        { meta: { globalError: false, context: 'import-pam-costed-actions' } }
      );
      return response.data.actions;
    },
    enabled: open && step === 3 && primarySubmissionId !== ''
  });

  const saqById = useMemo(() => new Map(selectedSaqs.map((saq) => [saq.submission_saq_id, saq])), [selectedSaqs]);
  const assignedSelectedSaqs = selectedSaqs.filter((saq) => saq.assignedToThisGroup);
  const saqsToAssign = selectedSaqs.filter((saq) => !saq.assignedToThisGroup);

  const saqActions: PamCostedAction[] = useMemo(() => {
    const ids = new Set(selectedSaqIds);
    return (costedQuery.data ?? []).filter((action) => ids.has(action.submission_saq_id));
  }, [costedQuery.data, selectedSaqIds]);

  const unusedPamFiles = useMemo(() => {
    const selectedIds = new Set(Object.values(fileSelections).flat());
    return pamFiles.filter((file) => !selectedIds.has(file.document_id));
  }, [pamFiles, fileSelections]);

  const unusedPamNotes = useMemo(() => {
    const selectedIds = new Set(Object.values(noteSelections).flat());
    return pamNotes.filter((note) => !selectedIds.has(note.id));
  }, [pamNotes, noteSelections]);

  const { actionableRequirements, emptyRequirements } = useMemo(() => {
    const actionable: StatutoryDocumentRow[] = [];
    const empty: StatutoryDocumentRow[] = [];
    for (const requirement of requirementsForSaq) {
      if (requirementHasPamItems(pamFiles, pamNotes, requirement)) actionable.push(requirement);
      else empty.push(requirement);
    }
    return { actionableRequirements: actionable, emptyRequirements: empty };
  }, [requirementsForSaq, pamFiles, pamNotes]);

  const toggleFile = (recommendationId: string, documentId: string) => {
    setFileSelections((current) => {
      const selected = new Set(current[recommendationId] ?? []);
      if (selected.has(documentId)) selected.delete(documentId);
      else selected.add(documentId);
      return { ...current, [recommendationId]: [...selected] };
    });
  };

  const toggleNote = (recommendationId: string, noteId: string) => {
    setNoteSelections((current) => {
      const selected = new Set(current[recommendationId] ?? []);
      if (selected.has(noteId)) selected.delete(noteId);
      else selected.add(noteId);
      return { ...current, [recommendationId]: [...selected] };
    });
  };

  const handleConfirm = async () => {
    const selectedDocumentIds = [...new Set(Object.values(fileSelections).flat())];
    const filesById = new Map(pamFiles.map((file) => [file.document_id, file]));
    const toLink = selectedDocumentIds.filter((id) => {
      const file = filesById.get(id);
      return file != null && isOrgLibraryFile(file);
    });
    const toMove = selectedDocumentIds.filter((id) => {
      const file = filesById.get(id);
      return file != null && !isOrgLibraryFile(file);
    });
    const notesById = new Map(pamNotes.map((note) => [note.id, note]));

    setImporting(true);
    try {
      if (toLink.length > 0) {
        await hubApi.linkOrganisationLibraryDocuments(groupId, toLink);
      }
      if (toMove.length > 0) {
        await hubApi.moveDocumentsToGroup(groupId, toMove);
      }
      const links = Object.entries(fileSelections)
        .filter(([, documentIds]) => documentIds.length > 0)
        .map(([recommendationId, documentIds]) => ({
          recommendation_id: recommendationId,
          document_ids: documentIds
        }));
      const notes = Object.entries(noteSelections).flatMap(([recommendationId, noteIds]) => {
        const requirement = documents.find((row) => row.recommendation_id === recommendationId);
        if (requirement == null) return [];
        return noteIds.flatMap((noteId) => {
          const note = notesById.get(noteId);
          if (note == null || noteAlreadyOnRequirement(requirement, note)) return [];
          return [
            {
              recommendation_id: recommendationId,
              text: note.text,
              contact_name: note.contact_name,
              contact_details: note.contact_details,
              external_link: note.external_link
            }
          ];
        });
      });
      if (notes.length === 0) {
        if (links.length > 0) {
          await importGroupStatutoryEvidence(groupId, { links, notes });
        }
      } else {
        for (let offset = 0; offset < notes.length; offset += STATUTORY_IMPORT_NOTE_BATCH_SIZE) {
          await importGroupStatutoryEvidence(groupId, {
            links: offset === 0 ? links : [],
            notes: notes.slice(offset, offset + STATUTORY_IMPORT_NOTE_BATCH_SIZE)
          });
        }
      }
      const saqsToBind = saqsToAssign.filter(
        (saq) => saq.canAssign && saq.submission_id !== '' && !saq.submission_saq_id.startsWith('domain-default:')
      );
      for (const saq of saqsToBind) {
        await pamApi.updateSaqGroup(saq.submission_id, saq.submission_saq_id, { group_id: groupId });
      }
      void queryClient.invalidateQueries({ queryKey: ['group-statutory-documents', groupId] });
      void queryClient.invalidateQueries({ queryKey: ['document-hub'] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.groupActions(groupId) });
      void queryClient.invalidateQueries({ queryKey: pamQueryKeys.groupSaqs(groupId) });
      const fileCount = selectedDocumentIds.length;
      const noteCount = Object.values(noteSelections).reduce((sum, ids) => sum + ids.length, 0);
      const parts: string[] = [];
      if (fileCount > 0) parts.push(`${fileCount} file${fileCount === 1 ? '' : 's'}`);
      if (noteCount > 0) parts.push(`${noteCount} note${noteCount === 1 ? '' : 's'}`);
      if (saqsToBind.length > 0) {
        parts.push(`${saqsToBind.map((saq) => saq.acronym).join(', ')} (PAM actions follow the SAQ)`);
      }
      toast.success(
        parts.length > 0
          ? `Imported ${parts.join(' and ')} into ${groupName}.`
          : `Nothing new to import into ${groupName}.`
      );
      onClose();
    } catch (error: unknown) {
      const detail = (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      toast.error(typeof detail === 'string' && detail.trim() ? detail : 'Failed to import PAM evidence');
    } finally {
      setImporting(false);
    }
  };

  const saqsLoading = groupSaqsQuery.isFetching || submissionsQuery.isFetching || overviewQuery.isFetching;
  const canContinueFromSaq = selectedSaqs.some((saq) => saq.canOpen);
  const evidenceLoading =
    step === 2 &&
    (evidenceQuery.isFetching || notesQuery.isFetching || (mappedQuestionIds.length === 0 && overviewQuery.isFetching));
  const actionsLoading = step === 3 && costedQuery.isFetching;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Import from PAM (${step} of 3)`}
      className="w-full max-w-4xl min-h-[560px] h-[80vh]"
      overflowHidden
      aria-label="Import from PAM"
    >
      <div className="flex min-h-0 flex-1 flex-col gap-4">
        <p className="text-sm text-content-subtle shrink-0">
          {step === 1 &&
            `Add PAM SAQs to import from. SAQs already assigned to ${groupName} are included automatically. Extra SAQs will be assigned to this group when you confirm.`}
          {step === 2 &&
            'For each evidence requirement, pick the PAM files, notes, and links from the matching question. Organisation-library files stay linked; other files (including personal PAM uploads) move into this group so members can open them.'}
          {step === 3 &&
            `PAM actions follow the SAQ’s group. Actions on SAQs already assigned to ${groupName} stay here. Confirming will assign any other selected SAQs to this group and move their actions.`}
        </p>

        <div className="min-h-0 flex-1 overflow-y-auto pr-1">
          {step === 1 && (
            <div className="flex flex-col gap-4">
              {saqsLoading ? (
                <Loading text="Loading PAM SAQs…" />
              ) : saqOptions.length === 0 ? (
                <p className="text-sm text-content-subtle">No PAM SAQs are available to import from.</p>
              ) : (
                <>
                  {submissions.length > 1 && (
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs font-medium text-content-subtle">PAM submission</label>
                      <Dropdown
                        ariaLabel="PAM submission"
                        width="w-full"
                        value={submissionId}
                        options={submissions.map((item) => ({
                          value: item.submission_id,
                          label: submissionOptionLabel(item)
                        }))}
                        onChange={(value) => {
                          if (typeof value !== 'string' || value === submissionId) return;
                          setSubmissionOverride(value);
                          setSelectedSaqIds([]);
                          setFileSelections({});
                          setNoteSelections({});
                          didPrefillSaqs.current = false;
                        }}
                      />
                    </div>
                  )}
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor={saqSearchId} className="text-xs font-medium text-content-subtle">
                      Add PAM SAQ
                    </label>
                    <Combobox
                      variant="field"
                      fullWidth
                      id={saqSearchId}
                      options={addableSaqOptions.map((saq) => ({
                        value: saq.submission_saq_id,
                        label: saq.assignedToThisGroup
                          ? `${saq.acronym} — ${saq.name} (assigned to this group)`
                          : `${saq.acronym} — ${saq.name}`,
                        disabled: !saq.canOpen,
                        hint: !saq.canOpen
                          ? 'You don’t have access to open this SAQ in PAM'
                          : !saq.canAssign && !saq.assignedToThisGroup
                            ? 'You can’t assign this SAQ to the group'
                            : undefined
                      }))}
                      value={saqQuery}
                      onSearch={setSaqQuery}
                      onSelect={(value) => {
                        if (typeof value !== 'string') return;
                        const option = saqOptions.find((saq) => saq.submission_saq_id === value);
                        if (option != null && !option.canOpen) return;
                        setSelectedSaqIds((current) => (current.includes(value) ? current : [...current, value]));
                        setSaqQuery('');
                        setFileSelections({});
                        setNoteSelections({});
                      }}
                      placeholder="Search by acronym or name…"
                      emptyText="No matching SAQs"
                    />
                  </div>
                  {selectedSaqs.length > 0 ? (
                    <div className="flex flex-col gap-2">
                      <p className="text-xs font-medium text-content-subtle">Selected</p>
                      {selectedSaqs.map((saq) => (
                        <div
                          key={saq.submission_saq_id}
                          className={`flex items-start justify-between gap-3 rounded-lg border border-border-subtle px-3 py-2.5 ${
                            saq.canOpen ? '' : 'opacity-60'
                          }`}
                        >
                          <span className="min-w-0">
                            <span
                              className={`flex items-center gap-2 text-sm font-medium ${
                                saq.canOpen ? 'text-content-default' : 'text-content-subtle'
                              }`}
                            >
                              <BuildingIcon size={16} className="shrink-0 text-secondary-blue" />
                              {saq.acronym}
                              <span className="font-normal text-content-subtle"> · {saq.name}</span>
                            </span>
                            <span className="mt-1.5 flex flex-wrap gap-1.5">
                              {!saq.canOpen ? (
                                <Pill size="sm" className="bg-surface-hover text-content-subtle">
                                  No PAM access
                                </Pill>
                              ) : saq.assignedToThisGroup ? (
                                <Pill size="sm" className="bg-surface-hover text-content-subtle">
                                  Assigned to this group
                                </Pill>
                              ) : saq.canAssign ? (
                                <Pill size="sm" className="bg-amber-50 text-amber-800">
                                  Will assign to this group
                                </Pill>
                              ) : (
                                <Pill size="sm" className="bg-surface-hover text-content-subtle">
                                  Cannot assign
                                </Pill>
                              )}
                              {suggestedAcronym != null && saq.acronym === suggestedAcronym && (
                                <Pill size="sm" className="bg-bluegray-100 text-content-default">
                                  Default for this safety group
                                </Pill>
                              )}
                            </span>
                            <p className="mt-1.5 text-xs text-content-subtle">
                              {!saq.canOpen
                                ? `You don’t have access to open ${saq.acronym} in PAM, so its files and actions can’t be imported.`
                                : saq.assignedToThisGroup
                                  ? `This group already owns ${saq.acronym}. Evidence can still be copied onto the requirements list.`
                                  : saq.canAssign
                                    ? `Confirming will assign ${saq.acronym} to ${groupName} and move its PAM actions here.`
                                    : `You can copy evidence from ${saq.acronym}, but you don’t have permission to assign it to ${groupName}.`}
                            </p>
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            icon={XIcon}
                            aria-label={`Remove ${saq.acronym}`}
                            onClick={() => {
                              setSelectedSaqIds((current) => current.filter((id) => id !== saq.submission_saq_id));
                              setFileSelections({});
                              setNoteSelections({});
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-content-subtle">Add at least one SAQ to import from.</p>
                  )}
                </>
              )}
            </div>
          )}

          {step === 2 && selectedSaqs.length > 0 && (
            <div className="flex flex-col gap-3">
              {evidenceLoading ? (
                <Loading text="Loading PAM evidence…" />
              ) : requirementsForSaq.length === 0 ? (
                <p className="text-sm text-content-subtle">
                  None of this group&apos;s evidence requirements come from{' '}
                  {selectedSaqs.map((saq) => saq.acronym).join(', ')}.
                </p>
              ) : (
                <>
                  <div className="overflow-hidden rounded-xl border border-border-subtle">
                    <table className="w-full table-fixed border-collapse text-left">
                      <thead className="bg-slate-50">
                        <tr className="border-b border-border-subtle">
                          <th className="w-[32%] px-3 py-2 text-xs font-medium text-content-subtle">Requirement</th>
                          <th className="w-[22%] px-3 py-2 text-xs font-medium text-content-subtle">Linked PAM</th>
                          <th className="px-3 py-2 text-xs font-medium text-content-subtle">Proposed evidence</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border-subtle">
                        {actionableRequirements.map((requirement) => (
                          <PamImportRequirementRow
                            key={requirement.recommendation_id}
                            requirement={requirement}
                            pamFiles={pamFiles}
                            pamNotes={pamNotes}
                            fileSelections={fileSelections}
                            noteSelections={noteSelections}
                            muted={false}
                            onToggleFile={toggleFile}
                            onToggleNote={toggleNote}
                          />
                        ))}
                        {actionableRequirements.length > 0 && emptyRequirements.length > 0 && (
                          <tr>
                            <td colSpan={3} className="bg-white px-3 py-2 text-center text-xs text-content-subtle">
                              No PAM evidence to import
                            </td>
                          </tr>
                        )}
                        {emptyRequirements.map((requirement) => (
                          <PamImportRequirementRow
                            key={requirement.recommendation_id}
                            requirement={requirement}
                            pamFiles={pamFiles}
                            pamNotes={pamNotes}
                            fileSelections={fileSelections}
                            noteSelections={noteSelections}
                            muted
                            onToggleFile={toggleFile}
                            onToggleNote={toggleNote}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {!evidenceLoading && (unusedPamFiles.length > 0 || unusedPamNotes.length > 0) && (
                    <div className="rounded-lg border border-dashed border-border-subtle p-3">
                      <p className="text-sm font-medium text-content-default">Unused PAM evidence</p>
                      <p className="text-xs text-content-subtle">Not ticked on any requirement above.</p>
                      <ul className="mt-2 flex flex-col gap-1">
                        {unusedPamFiles.map((file) => (
                          <li key={file.document_id} className="flex items-center gap-2 text-sm text-content-subtle">
                            <FileTextIcon size={16} className="shrink-0" />
                            <span className="truncate">{file.name}</span>
                          </li>
                        ))}
                        {unusedPamNotes.map((note) => {
                          const NoteIcon = otherEvidenceIcon(note);
                          return (
                            <li key={note.id} className="flex items-center gap-2 text-sm text-content-subtle">
                              <NoteIcon size={16} weight="light" className="shrink-0" />
                              <span className="truncate">{otherEvidenceLabel(note)}</span>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="flex flex-col gap-4">
              {actionsLoading ? (
                <Loading text="Loading PAM actions…" />
              ) : (
                <>
                  {assignedSelectedSaqs.length > 0 && (
                    <div className="flex flex-col gap-2">
                      <p className="text-sm font-medium text-content-default">Already assigned to this group</p>
                      <p className="text-xs text-content-subtle">
                        {assignedSelectedSaqs.map((saq) => saq.acronym).join(', ')} already belong to {groupName}. Their
                        PAM actions stay on this group.
                      </p>
                      {saqActions.filter((action) => saqById.get(action.submission_saq_id)?.assignedToThisGroup)
                        .length === 0 ? (
                        <p className="text-sm text-content-subtle">No PAM actions on these SAQs.</p>
                      ) : (
                        saqActions
                          .filter((action) => saqById.get(action.submission_saq_id)?.assignedToThisGroup)
                          .map((action) => <PamCostedActionRow key={action.action_id} action={action} />)
                      )}
                    </div>
                  )}
                  {saqsToAssign.length > 0 && (
                    <div className="flex flex-col gap-2">
                      <p className="text-sm font-medium text-content-default">Assign to this group</p>
                      <p className="text-xs text-content-subtle">
                        {saqsToAssign.map((saq) => saq.acronym).join(', ')} {saqsToAssign.length === 1 ? 'is' : 'are'}{' '}
                        not assigned to {groupName} yet. Confirming will assign{' '}
                        {saqsToAssign.length === 1 ? 'it' : 'them'} to this group; these PAM actions will follow.
                      </p>
                      {saqActions.filter((action) => !saqById.get(action.submission_saq_id)?.assignedToThisGroup)
                        .length === 0 ? (
                        <p className="text-sm text-content-subtle">No PAM actions on these SAQs.</p>
                      ) : (
                        saqActions
                          .filter((action) => !saqById.get(action.submission_saq_id)?.assignedToThisGroup)
                          .map((action) => <PamCostedActionRow key={action.action_id} action={action} />)
                      )}
                    </div>
                  )}
                  {saqActions.length === 0 && assignedSelectedSaqs.length === 0 && saqsToAssign.length === 0 && (
                    <p className="text-sm text-content-subtle">No PAM actions are listed for the selected SAQs.</p>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        <div className="flex shrink-0 justify-end gap-2 border-t border-border-subtle pt-3">
          {step > 1 ? (
            <Button variant="outline" onClick={() => setStep((current) => (current === 3 ? 2 : 1))}>
              Back
            </Button>
          ) : (
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
          )}
          {step < 3 ? (
            <Button
              variant="primary"
              disabled={step === 1 ? !canContinueFromSaq : evidenceLoading}
              onClick={() => setStep((current) => (current === 1 ? 2 : 3))}
            >
              Continue
            </Button>
          ) : (
            <Button variant="primary" disabled={actionsLoading || importing} onClick={() => void handleConfirm()}>
              {importing ? 'Importing…' : 'Confirm import'}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}
